import { useMemo, useState } from 'react';

import { Flag02Icon } from '@hugeicons-pro/core-solid-rounded';
import { ArrowDown01Icon, BubbleChatQuestionIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { FieldTypes } from 'constants/field-types-enum';
import { FieldTypesOptions } from 'constants/field-types-enum';
import { FLAG_SEVERITY_CARD_STYLES, FLAG_SEVERITY_MAP } from 'constants/forms';
import { saveAs } from 'file-saver';
import {
  downloadAllAsZip as downloadAllAsZipUtil,
  getFilenameFromUrl,
} from 'helpers/document-download-helpers';

import { Checkbox } from 'components/FormComponents';
import StatusPill from 'components/Pills/StatusPills';

import { useFormattedAnswer } from '../../hooks/useFormattedAnswer';
import styles from './QuestionResponse.module.scss';

export const QuestionResponse = ({
  order,
  field,
  answer,
  params = {},
  flagSeverity,
  language,
  languageCode,
  translatedText,
  translationScore,
}) => {
  const [zipLoading, setZipLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const isStatement = field?.type === FieldTypes.Statement;
  const flagStyles =
    flagSeverity && FLAG_SEVERITY_MAP[flagSeverity]
      ? FLAG_SEVERITY_CARD_STYLES[flagSeverity]
      : null;

  const download = useMemo(
    () => url => {
      const targetUrl = url ?? answer;
      if (!targetUrl) return;
      saveAs(targetUrl, getFilenameFromUrl(targetUrl, 0));
    },
    [answer],
  );

  const downloadAllAsZip = useMemo(
    () =>
      (urls, zipName = 'documents.zip') =>
        downloadAllAsZipUtil(urls, zipName, {
          onStart: () => setZipLoading(true),
          onEnd: () => setZipLoading(false),
        }),
    [],
  );

  const formattedAnswer = useFormattedAnswer(field, answer, {
    onDownload: download,
    onDownloadAllAsZip: downloadAllAsZip,
    zipLoading,
  });

  const questionText = useMemo(() => {
    const text = field.text;

    if (!text) return;

    const textWithLinks = text.replace(/\[((?!\]).)*\]\(((?!\)).)*\)/g, match => {
      const [, text] = /\[([^)]+)\]/.exec(match);
      const [, link] = /\(([^)]+)\)/.exec(match);

      return `<a href="${link}" class='u-link dark' target="_blank">${text}</a>`;
    });

    const textWithVars = textWithLinks.replace(/{{((?!}).)*}}/g, match => {
      const m = match.replace(/[{}]/g, '');
      const value = params[m];
      const text = value ? value : isNaN(m) ? m : `Q${m} answer`;

      return `<b>${text}</b>`;
    });

    return textWithVars;
  }, [field, params]);

  return (
    <div
      className={cn(
        'transition-all duration-300 rounded-2xl border',
        flagStyles
          ? `${flagStyles.bg} ${flagStyles.border} hover:opacity-95`
          : 'bg-neutral-1 border-neutral-4 hover:border-neutral-5',
        isStatement && 'cursor-pointer group',
      )}
      onClick={isStatement ? () => setIsExpanded(prev => !prev) : undefined}
      role={isStatement ? 'button' : undefined}>
      <div className="p-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-neutral-1 text-lg font-medium bg-neutral-6 rounded-full w-10 h-10 flex items-center justify-center">
                  {order || field?.order}
                </span>
                <div className="flex items-center gap-2">
                  <HugeiconsIcon
                    icon={
                      FieldTypesOptions.find(option => option.key === field.type)?.icon ??
                      BubbleChatQuestionIcon
                    }
                    size={20}
                    className="text-text-tertiary"
                  />
                  <span className="text-lg font-regular text-neutral-6 uppercase tracking-wide">
                    {FieldTypesOptions.find(option => option.key === field.type)?.title}
                  </span>
                </div>
              </div>
              {flagSeverity && FLAG_SEVERITY_MAP[flagSeverity] && (
                <StatusPill
                  text={FLAG_SEVERITY_MAP[flagSeverity].label}
                  status={FLAG_SEVERITY_MAP[flagSeverity].status}
                  icon={Flag02Icon}
                  iconColor={flagStyles.color}
                  small
                  className="px-4 py-2 border-none"
                />
              )}
            </div>
            {field.type === FieldTypes.Checkbox ? (
              <Checkbox
                name={`${field.id}`}
                checked={!!answer}
                onChange={() => {}}
                label={questionText}
                className="pointer-events-none cursor-none"
              />
            ) : (
              <div
                className={cn(
                  'text-2xl font-medium text-neutral-7 leading-snug min-w-0',
                  isStatement && !isExpanded && styles.statementClamp,
                )}
                dangerouslySetInnerHTML={{ __html: questionText }}
              />
            )}
          </div>
        </div>
        {formattedAnswer != null && field.type !== FieldTypes.Checkbox && (
          <div className="text-xl">
            {translatedText ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl font-medium text-neutral-7">
                      English translation (auto-generated)
                    </span>
                    {translationScore != null && (
                      <StatusPill
                        text={'Confidence: ' + Math.round(translationScore * 100) + '%'}
                        status={translationScore >= 0.7 ? 'success' : 'warning'}
                        small
                      />
                    )}
                  </div>
                  <div className={styles.answer}>{translatedText}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl font-medium text-neutral-7">Original response</span>
                    <StatusPill
                      text={language || languageCode || 'Translated'}
                      status="info"
                      small
                    />
                  </div>
                  <div className={cn('rounded-3xl border p-4 bg-neutral-2', styles.answer)}>
                    {formattedAnswer}
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.answer}>{formattedAnswer}</div>
            )}
          </div>
        )}
      </div>
      {isStatement && (
        <div className="px-6 py-4 border-t border-neutral-4 flex items-center justify-between">
          <div className="text-lg font-regular text-neutral-6">
            {isExpanded ? 'Click to collapse' : 'Click to expand'}
          </div>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={20}
            className="text-neutral-6 transition-transform duration-300"
            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        </div>
      )}
    </div>
  );
};
